/* eslint-disable @typescript-eslint/no-explicit-any */
import { Img, PageTitle, ReactHookFormInput, ReactHookFormTextArea } from '@okam/brand-ui/index'
import Button from '@okam/brand-ui/src/components/Button'
import Checkbox from '@okam/brand-ui/src/components/Checkbox'
import ArrowDownUploadFile from '@okam/brand-ui/src/components/icons/ArrowDownUploadFile'
import Paragraph from '@okam/brand-ui/src/components/Paragraph'
import { useHtml2React } from '@okam/html2react/provider'
import axios from 'axios'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import tw, { css } from 'twin.macro' /* eslint-disable-line */
import { trackFormSubmission } from 'gtm-analytics/tracking'
import type { TMdxBlock } from '../../types'
import Form from './Form'
import type { DataStoryForm } from './interface'

const FormStoryPetParentsCornerBlock = (props: TMdxBlock) => {
  const { title } = props

  // translations keys
  const { t } = useTranslation('common')
  const firstNameLabel = t('FORM.STORY_FORM.FIRST_NAME_LABEL')
  const lastNameLabel = t('FORM.STORY_FORM.LAST_NAME_LABEL')
  const emailLabel = t('FORM.STORY_FORM.EMAIL_LABEL')
  const petNameLabel = t('FORM.STORY_FORM.PET_NAME_LABEL')
  const subjectLabel = t('FORM.STORY_FORM.SUBJECT_LABEL')
  const storyLabel = t('FORM.STORY_FORM.STORY_LABEL')
  const termsLabel = t('FORM.STORY_FORM.TERMS_LABEL')
  const fileAddLabel = t('FORM.STORY_FORM.FILE_ADD_LABEL')
  const uploadFileLabel = t('FORM.STORY_FORM.FILE_UPLOAD_LABEL')
  const { parseHTMLToReact } = useHtml2React()
  // states
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [selectedFileName, setFileName] = useState('')
  const [fileError, setFileError] = useState(false)
  const [formErrors, setFormErrors] = useState<{ code: string; field: string; message: string }[]>([])
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    pet_name: '',
    story: '',
    subject: '',
    terms_accepted: false,
    picture: null,
    language: '',
  })

  // form id
  const formId = process.env.NEXT_PUBLIC_STORY_FORM_ID ?? ''

  const { locale } = useRouter()

  const handleFormSubmit = async (data: DataStoryForm) => {
    if (!termsAccepted) {
      return
    }

    if (!data.picture) {
      setFileError(true)
      return
    }

    const dataFormStory = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      pet_name: data.pet_name,
      story: data.story,
      subject: data.subject,
      terms_accepted: termsAccepted,
      picture: data.picture,
      language: data.language,
    }

    try {
      await axios.post(`https://formspree.io/f/${formId}`, dataFormStory, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setIsFormSubmitted(true)
      trackFormSubmission(formData.email, 'histoires à croquer')
    } catch (error: any) {
      if (error.response) {
        setFormErrors(error.response.data.errors)
      }
    }
  }

  const handleCheckboxChange = (value: boolean) => {
    setTermsAccepted(value)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }))
  }

  const handleFileChange = (event: any) => {
    const file = event.target.files[0]
    setFormData((prevFormData) => ({
      ...prevFormData,
      picture: file,
    }))
    setFileName(file.name)
  }

  return (
    <div tw=" relative pb-16 lg:pb-32 px-6  bg-[#FFF8F2] overflow-hidden">
      {title && <PageTitle tw="pb-6 pt-16">{parseHTMLToReact(title)}</PageTitle>}
      <div tw="flex justify-center">
        <Paragraph as="p" variant="leading" tw="z-10 pb-10 w-full lg:w-1/2 text-center">
          {t('FORM.STORY_FORM.SUBTITLE')}
        </Paragraph>
      </div>
      {isFormSubmitted && (
        <Paragraph as="p" variant="leading" tw="relative pb-10 w-full z-10 text-center">
          {t('FORM.STORY_FORM.STORY_SEND')}
        </Paragraph>
      )}
      <div tw="absolute left-0 top-[55%] md:top-[30%] w-1/2 h-full 2xl:w-1/3">
        <Img
          css={css`
            clip-path: ellipse(80% 45% at 9% 50%);
          `}
          src="/shared-assets/images/texture-cross.svg"
          tw="absolute w-full object-cover"
          alt="texture"
          width={1200}
          height={1200}
        />
      </div>
      {!isFormSubmitted && (
        <Form onSubmit={() => handleFormSubmit(formData)} tw="w-full flex justify-center">
          <div tw="max-w-[34rem]">
            <div tw="flex flex-wrap -mx-3 mb-6">
              <div tw="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                <ReactHookFormInput
                  name="first_name"
                  required
                  ariaLabel={firstNameLabel}
                  placeholder={firstNameLabel}
                  id="terms-firstName"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
              </div>
              <div tw="w-full md:w-1/2 px-3">
                <ReactHookFormInput
                  name="last_name"
                  required
                  ariaLabel={lastNameLabel}
                  placeholder={lastNameLabel}
                  id="terms-lastName"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div tw="flex flex-wrap -mx-3 mb-6">
              <div tw="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                <ReactHookFormInput
                  name="email"
                  required
                  type="email"
                  ariaLabel={emailLabel}
                  placeholder={emailLabel}
                  id="terms-email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                {formErrors.length > 0 && formErrors[0].field === 'email' && <p>{formErrors[0].message}</p>}
              </div>
              <div tw="w-full md:w-1/2 px-3">
                <ReactHookFormInput
                  tw="text-center"
                  name="pet_name"
                  required
                  ariaLabel={petNameLabel}
                  placeholder={petNameLabel}
                  id="terms-petName"
                  value={formData.pet_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div tw="flex flex-wrap -mx-3 mb-6">
              <div tw="w-full px-3">
                <label
                  htmlFor="terms-picture"
                  tw="font-body flex items-center cursor-pointer justify-between rounded-3xl px-6 h-12 focus:outline-none border-none bg-white text-[#A5A5A5]"
                >
                  {!selectedFileName && uploadFileLabel}
                  <Paragraph as="p" variant="p" tw="text-[#A5A5A5]">
                    {selectedFileName && fileAddLabel}
                  </Paragraph>
                  <Paragraph as="p" variant="p" tw="mr-2">
                    <ArrowDownUploadFile tw="w-5 h-5" />
                  </Paragraph>
                </label>
                <input
                  tw="hidden"
                  type="file"
                  accept="image/png, image/jpeg"
                  name="picture"
                  aria-label={uploadFileLabel}
                  placeholder={uploadFileLabel}
                  id="terms-picture"
                  onChange={handleFileChange}
                />

                {fileError && (
                  <p tw="flex items-center justify-between rounded-3xl pl-4 h-12 focus:outline-none border-none text-red-500">
                    {t('FORM.REQUIRED')}
                  </p>
                )}
              </div>
            </div>
            <div tw="flex flex-wrap -mx-3 mb-6">
              <div tw="w-full px-3">
                <ReactHookFormInput
                  name="subject"
                  required
                  ariaLabel={subjectLabel}
                  placeholder={subjectLabel}
                  id="terms-subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div tw="flex flex-wrap -mx-3 mb-6">
              <div tw="w-full px-3">
                <ReactHookFormTextArea
                  name="story"
                  required
                  ariaLabel={storyLabel}
                  placeholder={storyLabel}
                  id="terms-story"
                  value={formData.story}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div tw="md:flex md:items-center mb-6">
              <Checkbox
                onChange={handleCheckboxChange}
                name="terms_accepted"
                required
                id="terms-accepted"
                ariaLabel={termsLabel}
                label={termsLabel}
              />
            </div>
            <input type="hidden" name="language" value={locale ?? 'fr'} />

            <div tw="mb-6 text-center flex justify-center">
              <Button type="submit" disabled={!termsAccepted} tw="text-white bg-[#F7643B] hover:bg-[#F7643B]">
                {t('FORM.STORY_FORM.SUBMIT_BUTTON_LABEL')}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </div>
  )
}

export default FormStoryPetParentsCornerBlock
